import React, { useState, useCallback, useEffect } from 'react';
import './PhotoUploader.css';

const PhotoUploader = ({
    maxPhotos = 1,
    onPhotosSelected,
    onCancel,
    title = "Cargar Fotos",
    description = "Selecciona o arrastra las fotos aquí",
    acceptMultiple = false,
    showPreview = true,
    allowRemove = true,
    requireConfirmation = false
}) => {
    const [selectedPhotos, setSelectedPhotos] = useState([]);
    const [dragActive, setDragActive] = useState(false);

    const isMultiple = maxPhotos > 1 || acceptMultiple;

    const handleFileSelect = useCallback((files) => {
        const validFiles = Array.from(files).filter(file =>
            file.type.startsWith('image/')
        );

        if (validFiles.length === 0) return;

        const filesToAdd = validFiles.slice(0, maxPhotos - selectedPhotos.length);

        const newPhotos = filesToAdd.map(file => ({
            id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            file: file,
            preview: URL.createObjectURL(file),
            name: file.name,
            size: file.size,
            uploadDate: new Date()
        }));

        setSelectedPhotos(prev => [...prev, ...newPhotos]);
    }, [maxPhotos, selectedPhotos.length]);

    const handleUploadClick = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = isMultiple;
        input.onchange = (e) => {
            if (e.target.files) {
                handleFileSelect(e.target.files);
            }
        };
        input.click();
    }, [isMultiple, handleFileSelect]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragActive(false);

        if (e.dataTransfer.files) {
            handleFileSelect(e.dataTransfer.files);
        }
    }, [handleFileSelect]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setDragActive(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setDragActive(false);
    }, []);

    const handleRemovePhoto = useCallback((photoId) => {
        setSelectedPhotos(prev => {
            const photoToRemove = prev.find(p => p.id === photoId);
            if (photoToRemove?.preview) {
                URL.revokeObjectURL(photoToRemove.preview);
            }
            return prev.filter(p => p.id !== photoId);
        });
    }, []);

    const handleConfirm = useCallback(() => {
        if (selectedPhotos.length > 0) {
            if (!requireConfirmation) {
                onPhotosSelected(selectedPhotos);
                setSelectedPhotos([]);
            } else {
                onPhotosSelected(selectedPhotos);
            }
        }
    }, [selectedPhotos, onPhotosSelected, requireConfirmation]);

    useEffect(() => {
        return () => {
            selectedPhotos.forEach(photo => {
                if (photo.preview) {
                    URL.revokeObjectURL(photo.preview);
                }
            });
        };
    }, [selectedPhotos]);

    return (
        <div className="photo-uploader">
            <div className="photo-uploader-header">
                <h3>{title}</h3>
                <p>{description}</p>
                {maxPhotos > 1 && (
                    <p className="photo-limit">
                        Máximo {maxPhotos} foto{maxPhotos > 1 ? 's' : ''}
                    </p>
                )}
            </div>

            <div
                className={`upload-zone ${dragActive ? 'drag-active' : ''} ${selectedPhotos.length >= maxPhotos ? 'disabled' : ''}`}
                onClick={selectedPhotos.length >= maxPhotos ? undefined : handleUploadClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
            >
                <div className="upload-content">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7,10 12,15 17,10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    <h4>Haz clic o arrastra la{isMultiple ? 's' : ''} foto{isMultiple ? 's' : ''} aquí</h4>
                    <p>Formatos soportados: JPG, PNG, GIF</p>
                    {selectedPhotos.length >= maxPhotos && (
                        <p className="limit-reached">Límite de fotos alcanzado</p>
                    )}
                </div>
            </div>

            {showPreview && selectedPhotos.length > 0 && (
                <div className="selected-photos-section">
                    <h4>Foto{isMultiple ? 's' : ''} Seleccionada{isMultiple ? 's' : ''} ({selectedPhotos.length})</h4>
                    <div className="selected-photos-grid">
                        {selectedPhotos.map(photo => (
                            <div key={photo.id} className="selected-photo-item">
                                <div className="photo-preview-container">
                                    <img
                                        src={photo.preview}
                                        alt={photo.name}
                                        className="photo-preview"
                                    />
                                    {allowRemove && (
                                        <div className="photo-overlay">
                                            <button
                                                className="remove-photo-btn"
                                                onClick={() => handleRemovePhoto(photo.id)}
                                                title="Eliminar foto"
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M18 6L6 18"></path>
                                                    <path d="M6 6l12 12"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="photo-info">
                                    <span className="photo-name">{photo.name}</span>
                                    <span className="photo-size">
                                        {(photo.size / 1024 / 1024).toFixed(2)} MB
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="photo-uploader-actions">
                <button 
                    className="btn btn-cancel"
                    onClick={onCancel}
                >
                    Cancelar
                </button>
                <button 
                    className={`btn btn-primary ${selectedPhotos.length === 0 ? 'disabled' : ''}`}
                    onClick={handleConfirm}
                    disabled={selectedPhotos.length === 0}
                >
                    {/* 🔧 MODIFICADO: Texto del botón según el modo */}
                    {requireConfirmation 
                        ? (isMultiple ? `Confirmar ${selectedPhotos.length} Foto${selectedPhotos.length !== 1 ? 's' : ''}` : 'Confirmar Foto')
                        : (isMultiple ? `Subir ${selectedPhotos.length} Foto${selectedPhotos.length !== 1 ? 's' : ''}` : 'Subir Foto')
                    }
                </button>
            </div>
        </div>
    );
};

export default PhotoUploader;